import React, { useState } from 'react';
import { 
    BookOpen, Globe, Target, Brain, Shield, 
    Download, ChevronRight, Sparkles, Moon, 
    Star, Zap, Pyramid, Infinity, Lock, 
    Eye, GraduationCap, Users, CheckCircle,
    PlayCircle, BookMarked, Calendar
} from 'lucide-react';

function VedikshhaSection() {
    const [activeTab, setActiveTab] = useState('overview');

    const features = [
        {
            icon: <BookOpen size={24} />,
            title: "Ancient Occult Wisdom",
            description: "Learn sacred teachings derived from Vedic, Tantric, and mystical traditions explained in modern format",
            professionalDesc: "Study foundational principles derived from Vedic, Tantric, and mystical lineages with historical context"
        },
        {
            icon: <Globe size={24} />,
            title: "Astrology & Cosmic Influence",
            description: "Understand planetary energies, karmic patterns, and celestial movements affecting human life",
            professionalDesc: "Understand planetary influences, karmic structures, and celestial patterns affecting human life"
        },
        {
            icon: <Target size={24} />,
            title: "Numerology & Vibrational Science",
            description: "Decode numbers, life paths, and destiny through the power of numerical vibrations",
            professionalDesc: "Explore the science of numbers, frequencies, and their impact on personality and life cycles"
        },
        {
            icon: <Brain size={24} />,
            title: "Spiritual Practices & Energy Work",
            description: "Learn meditative techniques, mantras, and energy practices for clarity and balance",
            professionalDesc: "Learn guided practices focused on mental clarity, energetic balance, and inner development"
        },
        {
            icon: <Shield size={24} />,
            title: "Symbols, Mantras & Sacred Knowledge",
            description: "Explore hidden meanings behind ancient symbols, yantras, and sacred sounds",
            professionalDesc: "Analyze the meaning, structure, and application of ancient symbols and sound-based practices"
        }
    ];

    const benefits = [
        "Authentic occult knowledge rooted in ancient traditions",
        "Simple explanations for complex mystical concepts",
        "Structured courses for beginners to advanced learners",
        "Learn anytime, anywhere through a mobile app",
        "Spiritual growth with ethical guidance and awareness"
    ];

    const targetAudience = [
        "Spiritual seekers and mystics",
        "Students of astrology, numerology, and occult sciences",
        "Yogis, healers, and meditation practitioners",
        "Anyone curious about hidden dimensions of life",
        "No prior experience required—only an open mind"
    ];

    const educationalPillars = [
        { icon: <BookMarked size={20} />, title: "Authentic Source-Based Knowledge", desc: "Rooted in traditional texts and teachings" },
        { icon: <GraduationCap size={20} />, title: "Structured Learning Paths", desc: "Progressive curriculum from basic to advanced" },
        { icon: <Users size={20} />, title: "Ethical Interpretation", desc: "Responsible approach to occult sciences" },
        { icon: <Globe size={20} />, title: "Modern Accessibility", desc: "Digital platform for contemporary learners" }
    ];

    return (
        <div className="bg-gradient-to-b from-gray-50 via-white to-emerald-50/30 relative">
            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white">
                <div className="absolute inset-0">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent animate-pulse"></div>
                <div className="absolute top-1/4 -left-20 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-600/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
                
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `linear-gradient(to right, #9f7aea 1px, transparent 1px),
                                        linear-gradient(to bottom, #9f7aea 1px, transparent 1px)`,
                        backgroundSize: '50px 50px'
                    }}></div>
                </div>
            </div>
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-800/40 backdrop-blur-sm rounded-full border border-emerald-600/30 mb-6">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                            <span className="text-emerald-200 text-sm font-medium">A Unit of KFE</span>
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl font-bold mb-1">
                            <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                                Vedikshha
                            </span>
                        </h1>
                        
                        <p className="text-2xl md:text-3xl font-light mb-1 max-w-3xl mx-auto leading-relaxed">
                            Unlock the Ancient Science of the{' '}
                            <span className="font-semibold text-emerald-200">Occult</span>
                        </p>
                        
                        <p className="text-xl text-emerald-100/80 max-w-2xl mx-auto">
                            Discover Hidden Wisdom. Transform Your Inner World.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-16">
                {/* Introduction */}
                <div className="mb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6">
                            <h2 className="text-3xl font-bold text-gray-900">
                                A Scholarly Approach to{' '}
                                <span className="text-emerald-600">Occult Science</span>
                            </h2>
                            <p className="text-gray-600 leading-relaxed">
                                Vedikshha is a sacred gateway to ancient occult sciences, designed for seekers who wish to understand the unseen forces that shape life, destiny, and consciousness. Rooted in timeless Vedic wisdom and mystical traditions, Vedikshha brings powerful spiritual knowledge to your fingertips through a modern mobile app experience.
                            </p>
                            <p className="text-gray-600 leading-relaxed">
                                Whether you are a beginner or an advanced practitioner, Vedikshha guides you step by step into the profound sciences that have been preserved for centuries by sages, mystics, and spiritual masters.
                            </p>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl">
                                        <Lock size={24} className="text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">What is Occult Science?</h3>
                                </div>
                                <p className="text-gray-600 mb-4">
                                    Occult science is the study of hidden knowledge—energies, symbols, cosmic laws, and spiritual practices that operate beyond the physical world.
                                </p>
                                <div className="space-y-3">
                                    {[
                                        "The connection between mind, energy, and destiny",
                                        "The influence of planets, numbers, and vibrations",
                                        "Inner awakening and self-mastery",
                                        "Protection, healing, and spiritual growth"
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                                            <span className="text-gray-700">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs Section */}
                <div className="mb-20">
                    <div className="flex flex-wrap gap-4 mb-8 border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-6 py-3 font-medium transition-all duration-300 ${activeTab === 'overview' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-emerald-500'}`}
                        >
                            <div className="flex items-center gap-2">
                                <BookOpen size={18} />
                                What You Will Learn
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('benefits')}
                            className={`px-6 py-3 font-medium transition-all duration-300 ${activeTab === 'benefits' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-emerald-500'}`}
                        >
                            <div className="flex items-center gap-2">
                                <CheckCircle size={18} />
                                Why Choose Vedikshha
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('audience')}
                            className={`px-6 py-3 font-medium transition-all duration-300 ${activeTab === 'audience' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-emerald-500'}`}
                        >
                            <div className="flex items-center gap-2">
                                <Users size={18} />
                                Who is Vedikshha For
                            </div>
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="min-h-[400px]">
                        {activeTab === 'overview' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => (
                                    <div key={index} className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-emerald-300 hover:shadow-lg transition-all duration-300">
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-lg">
                                                    <div className="text-emerald-600">{feature.icon}</div>
                                                </div>
                                                <h4 className="font-bold text-gray-900 text-lg">{feature.title}</h4>
                                            </div>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                {feature.description}
                                            </p>
                                            <div className="pt-4 border-t border-gray-100">
                                                <p className="text-xs text-emerald-700 font-medium">
                                                    Professional Approach: {feature.professionalDesc}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'benefits' && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-bold text-gray-900">Educational Philosophy</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {educationalPillars.map((pillar, index) => (
                                            <div key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-100">
                                                <div className="flex items-start gap-4">
                                                    <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg">
                                                        <div className="text-white">{pillar.icon}</div>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 mb-1">{pillar.title}</h4>
                                                        <p className="text-sm text-gray-600">{pillar.desc}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-bold text-gray-900">Key Benefits</h3>
                                    <div className="space-y-4">
                                        {benefits.map((benefit, index) => (
                                            <div key={index} className="flex items-start gap-4">
                                                <div className="p-1 bg-emerald-100 rounded-full mt-1">
                                                    <CheckCircle size={16} className="text-emerald-600" />
                                                </div>
                                                <span className="text-gray-700">{benefit}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                                        <p className="text-emerald-800 font-medium italic">
                                            "Vedikshha is not about superstition—it is about understanding higher laws of existence."
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'audience' && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-bold text-gray-900">Target Audience</h3>
                                    <div className="space-y-4">
                                        {targetAudience.map((item, index) => (
                                            <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-100 hover:border-emerald-200 transition-all duration-300">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-100 to-teal-100 flex items-center justify-center">
                                                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                                </div>
                                                <span className="text-gray-700">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <div className="bg-gradient-to-br from-emerald-900 to-teal-900 rounded-2xl p-8 text-white">
                                        <h3 className="text-2xl font-bold mb-6">About Vedikshha</h3>
                                        <div className="space-y-4">
                                            <p className="leading-relaxed">
                                                Vedikshha is a specialized mobile learning platform dedicated to the systematic study of occult science and esoteric knowledge. Developed with a balance of traditional wisdom and contemporary learning methods.
                                            </p>
                                            <p className="leading-relaxed">
                                                The focus remains on knowledge, self-awareness, and intellectual exploration, rather than ritualistic or superstitious approaches.
                                            </p>
                                            <div className="pt-6 border-t border-emerald-700/50">
                                                <p className="font-semibold text-emerald-200">
                                                    Vedikshha – Advancing the Study of Occult Science
                                                </p>
                                                <p className="text-sm text-emerald-300 mt-1">A Unit of KFE</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* CTA Section */}
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        {/* Left */}
                        <div className="p-10 lg:p-12">
                            <h2 className="text-3xl font-bold text-white mb-6">
                                Begin Your Journey with{' '}
                                <span className="text-emerald-200">Vedikshha</span>
                            </h2>
                            <p className="text-emerald-100 mb-8 leading-relaxed">
                                Step into the ancient world of occult science and rediscover knowledge that empowers, enlightens, and transforms.
                            </p>
                            
                            <div className="space-y-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-emerald-500/30 rounded-lg">
                                        <PlayCircle size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white">Structured Learning</h4>
                                        <p className="text-emerald-100 text-sm">From beginner to advanced levels</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-emerald-500/30 rounded-lg">
                                        <Calendar size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white">Learn At Your Pace</h4>
                                        <p className="text-emerald-100 text-sm">Access courses anytime, anywhere</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right */}
                        <div className="p-10 lg:p-12 bg-gradient-to-br from-emerald-700 to-teal-700">
                            <div className="h-full flex flex-col justify-center">
                                <div className="text-center">
                                    <h3 className="text-2xl font-bold text-white mb-4">
                                        Vedikshha – Where{' '}
                                        <span className="text-emerald-200">Ancient Wisdom</span>{' '}
                                        Meets{' '}
                                        <span className="text-emerald-200">Modern Learning</span>
                                    </h3>
                                    
                                    <button className="group relative px-8 py-4 bg-white text-emerald-700 font-bold rounded-xl hover:bg-emerald-50 transition-all duration-300 w-full max-w-sm mx-auto shadow-lg hover:shadow-xl flex items-center justify-center gap-3">
                                        <Download size={22} />
                                        <span className="text-lg">Download Vedikshha App</span>
                                        <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                                    </button>
                                    
                                    <p className="text-emerald-200 text-sm mt-6">
                                        Access the Vedikshha mobile app to begin your learning journey
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Final Note */}
                <div className="text-center mt-16">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-full border border-emerald-200">
                        <Sparkles size={18} className="text-emerald-600" />
                        <span className="text-emerald-700 font-medium">
                            A Credible Platform for Serious Learners
                        </span>
                    </div>
                    <p className="text-gray-600 mt-6 max-w-2xl mx-auto">
                        Vedikshha presents deep knowledge in a structured, ethical, and easy-to-understand way, 
                        making ancient occult sciences accessible to modern seekers.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default VedikshhaSection;